import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User as UserIcon, Mail, Shield, KeyRound, Check, AlertCircle } from 'lucide-react';
import TopBar from '../components/layout/TopBar';
import Card, { CardHeader } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import PageHeader from '../components/ui/PageHeader';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../api/client';

// Standalone profile page. Reachable from the user menu in TopBar across any
// shell (Hub or MEC). Renders its own minimal chrome (TopBar only — no
// Sidebar) so it works for every role without dragging in role-specific
// navigation. The change-password backend enforces 10-char minimum; we
// mirror that client-side.
export default function Profile() {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col bg-[var(--color-cream-100)] font-sans">
      <TopBar user={user || {}} />
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-[860px] mx-auto px-6 py-8">
          <PageHeader
            title="Your profile"
            description="Manage your account details and password. Need to change your name or role? Ask a super admin."
            actions={
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<ArrowLeft size={14} />}
                onClick={() => navigate(-1)}
              >
                Back
              </Button>
            }
          />

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-5">
            <AccountCard user={user} />
            <ChangePasswordCard />
          </div>
        </div>
      </main>
    </div>
  );
}

function AccountCard({ user }) {
  if (!user) return null;
  const fields = [
    { label: 'Full name', value: user.full_name || '—', icon: <UserIcon size={14} /> },
    { label: 'Email', value: user.email || '—', icon: <Mail size={14} /> },
    { label: 'Role', value: prettyRole(user.role), icon: <Shield size={14} /> },
  ];
  return (
    <Card>
      <CardHeader
        title="Account"
        description="Read-only. These come from your team's user record."
      />
      <ul className="space-y-3">
        {fields.map((f) => (
          <li key={f.label} className="flex items-start gap-3 py-2 border-b border-[var(--color-warm-100)] last:border-0">
            <span className="w-8 h-8 rounded-[var(--radius-sm)] bg-[var(--color-cream-200)] text-[var(--color-warm-600)] flex items-center justify-center shrink-0">
              {f.icon}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-bold uppercase tracking-[1px] text-[var(--color-warm-500)]">
                {f.label}
              </div>
              <div className="text-[14px] font-semibold text-[var(--color-warm-900)] mt-0.5 truncate">
                {f.value}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function prettyRole(role) {
  if (!role) return '—';
  return role
    .split('_')
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
}

function ChangePasswordCard() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const validate = () => {
    if (!current) return 'Enter your current password.';
    if (!next || next.length < 10) return 'New password must be at least 10 characters.';
    if (next === current) return 'New password must be different from the current password.';
    if (next !== confirm) return 'New password and confirmation do not match.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setSubmitting(true);
    try {
      await api.changePassword(current, next);
      setSuccess(true);
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (err) {
      setError(err.message || 'Could not change password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title="Change password"
        description="Use at least 10 characters. You'll stay signed in after the change."
      />
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Current password"
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          leftIcon={<KeyRound size={14} />}
          disabled={submitting}
          required
        />
        <Input
          label="New password"
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          leftIcon={<KeyRound size={14} />}
          hint="At least 10 characters."
          disabled={submitting}
          required
        />
        <Input
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          leftIcon={<KeyRound size={14} />}
          disabled={submitting}
          required
        />

        {error && (
          <div className="flex items-start gap-2 p-3 rounded-[var(--radius-md)] bg-[var(--color-danger-50)] border border-[var(--color-danger-100)] text-[13px] text-[var(--color-danger-700)]">
            <AlertCircle size={14} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="flex items-start gap-2 p-3 rounded-[var(--radius-md)] bg-[var(--color-recovery-50)] border border-[var(--color-recovery-100)] text-[13px] text-[var(--color-recovery-700)]">
            <Check size={14} className="shrink-0 mt-0.5" />
            <span>Password updated successfully.</span>
          </div>
        )}

        <div className="flex justify-end pt-1">
          <Button type="submit" loading={submitting} disabled={submitting}>
            Update password
          </Button>
        </div>
      </form>
    </Card>
  );
}
